import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ArrowUpRight, Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { BRAND, waHref } from "../lib/brand";

const services = [
  { to: "/website-design", label: "Lead-Generation Websites" },
  { to: "/seo-ai-search-optimization", label: "Local SEO for Trades" },
  { to: "/ai-automation", label: "Enquiry Automation" },
  { to: "/construction-marketing", label: "Construction Websites" },
  { to: "/trades-websites", label: "Trades & Home-Service Sites" },
  { to: "/ecommerce-development", label: "Ecommerce" },
  { to: "/website-maintenance", label: "Website Maintenance" },
];

const company = [
  { to: "/about", label: "About" },
  { to: "/case-studies", label: "Work" },
  { to: "/pricing", label: "Pricing" },
  { to: "/audit", label: "Free Website Audit" },
  { to: "/free-website-review", label: "Free Website Review" },
  { to: "/blog", label: "Blog" },
  { to: "/cities-served", label: "Cities Served" },
  { to: "/contact", label: "Contact" },
  { to: "/sitemap", label: "Sitemap" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're in. Welcome to the WebsiteAndGo briefing.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-black text-white border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 min-w-0">
            <div className="flex items-center gap-2.5 mb-6">
              <img src="/logo.png" alt="WebsiteAndGo logo" width="48" height="48" className="w-12 h-12 object-contain" loading="lazy" decoding="async" />
              <span className="font-display text-lg font-semibold tracking-tight">WebsiteAndGo</span>
            </div>
            <p className="text-white/60 max-w-md leading-relaxed break-words">
              Websites, local SEO, and enquiry automation for home-service, trade, and construction businesses across the US and UK. Founder-led. Real work only — no fake stats.
            </p>

            <form onSubmit={subscribe} className="mt-8 flex max-w-md gap-2 min-w-0" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="flex-1 min-w-0 bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#FF6A00]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="btn-primary text-sm shrink-0"
              >
                {loading ? "Joining…" : "Subscribe"}
              </button>
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm" data-testid="footer-offices">
              {/* US Office */}
              <div data-testid="footer-us-office">
                <div className="eyebrow mb-3">🇺🇸 US Office</div>
                <div className="space-y-2 text-white/70">
                  <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> Lincoln Metro, Nebraska</div>
                  <a href="tel:+18089985633" data-testid="footer-us-phone" className="flex items-start gap-2 hover:text-white">
                    <Phone className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> +1 808-998-5633
                  </a>
                  <a href={waHref('footer-us')} target="_blank" rel="noopener noreferrer" data-testid="footer-us-whatsapp" className="flex items-start gap-2 hover:text-white">
                    <MessageCircle className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> WhatsApp: +1 808-998-5633
                  </a>
                  <a href="mailto:contact@websiteandgo.com" data-testid="footer-us-email" className="flex items-start gap-2 hover:text-white break-all">
                    <Mail className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> contact@websiteandgo.com
                  </a>
                </div>
              </div>
              {/* UK Office */}
              <div data-testid="footer-uk-office">
                <div className="eyebrow mb-3">🇬🇧 UK Office</div>
                <div className="space-y-2 text-white/70">
                  <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> {BRAND.uk.address}</div>
                  <a href={BRAND.uk.phoneTel} data-testid="footer-uk-phone" className="flex items-start gap-2 hover:text-white">
                    <Phone className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> {BRAND.uk.phoneDisplay}
                  </a>
                  <a href={waHref('footer-uk', 'uk')} target="_blank" rel="noopener noreferrer" data-testid="footer-uk-whatsapp" className="flex items-start gap-2 hover:text-white">
                    <MessageCircle className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> WhatsApp: {BRAND.uk.phoneDisplay}
                  </a>
                  <a href="mailto:contact@websiteandgo.com" data-testid="footer-uk-email" className="flex items-start gap-2 hover:text-white break-all">
                    <Mail className="w-4 h-4 text-[#FF6A00] mt-0.5 shrink-0" /> contact@websiteandgo.com
                  </a>
                </div>
              </div>
              {/* Business hours */}
              <div className="sm:col-span-2">
                <div className="eyebrow mb-3">Hours</div>
                <div className="space-y-2 text-white/70">
                  <div className="flex items-start gap-2">
                    <span className="text-white/40 font-mono text-xs mt-1">Mon–Fri</span>
                    <span>09:00 – 18:00 CT</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-white/40 font-mono text-xs mt-1">Sat–Sun</span>
                    <span>By appointment</span>
                  </div>
                  <div className="text-white/50 text-xs pt-2">Remote-delivery agency serving service businesses across the US and UK.</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <div className="eyebrow mb-4">Services</div>
              <ul className="space-y-2">
                {services.map((s) => (
                  <li key={s.to}><Link to={s.to} className="text-white/80 hover:text-white">{s.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="eyebrow mb-4">Company</div>
              <ul className="space-y-2">
                {company.map((s) => (
                  <li key={s.to}><Link to={s.to} className="text-white/80 hover:text-white">{s.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="eyebrow mb-4">Get started</div>
              <p className="text-white/60 text-sm mb-4">Book a free 30-minute strategy call — no pressure, real value.</p>
              <a href={waHref('footer-cta')} target="_blank" rel="noopener noreferrer" data-testid="footer-cta" className="inline-flex items-center gap-2 text-[#FF6A00] hover:underline">
                Book strategy call <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-xs text-white/40">
          <div>© {new Date().getFullYear()} WebsiteAndGo. Crafted with care in Lincoln Metro, Nebraska.</div>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white/70">Privacy</Link>
            <Link to="/terms" className="hover:text-white/70">Terms</Link>
            <Link to="/admin/login" className="hover:text-white/70">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
